import { FC } from 'react';
import { useTheme } from '../context/ThemeContext.tsx';

export const AboutPage: FC = () => {
  const { theme } = useTheme();
  
  // Define types for better type safety
  type ThemeMode = 'light' | 'dark';
  type ValueIconType = 'trust' | 'innovation' | 'excellence' | 'global';
  
  interface ThemeStyle {
    background: string;
    text: string;
    card: string;
    cardText: string;
    sectionTitle: string;
    heroGradient: string;
    philosophyCard: string;
    philosophyTitle: string;
    missionCard: string;
    missionTitle: string;
    leaderCard: string;
    leaderName: string;
    leaderRole: string;
    leaderTitle: string;
    leaderBio: string;
    leaderExpertise: string;
    valueCard: string;
    valueTitle: string;
    valueDescription: string;
    valueIconBg: Record<ValueIconType, string>;
  }

  const themeStyles: Record<ThemeMode, ThemeStyle> = {
    light: {
      background: 'bg-gray-50',
      text: 'text-gray-900',
      card: 'bg-white',
      cardText: 'text-gray-700',
      sectionTitle: 'text-gray-800',
      heroGradient: 'from-blue-600 to-blue-800',
      philosophyCard: 'bg-blue-50',
      philosophyTitle: 'text-blue-800',
      missionCard: 'bg-green-50',
      missionTitle: 'text-green-800',
      leaderCard: 'bg-white',
      leaderName: 'text-gray-800',
      leaderRole: 'text-blue-600',
      leaderTitle: 'text-gray-500',
      leaderBio: 'text-gray-700',
      leaderExpertise: 'text-gray-600',
      valueCard: 'bg-white',
      valueTitle: 'text-gray-800',
      valueDescription: 'text-gray-600',
      valueIconBg: {
        trust: 'bg-blue-50 text-blue-600',
        innovation: 'bg-purple-50 text-purple-600',
        excellence: 'bg-green-50 text-green-600',
        global: 'bg-amber-50 text-amber-600'
      }
    },
    dark: {
      background: 'bg-gray-900',
      text: 'text-gray-100',
      card: 'bg-gray-800',
      cardText: 'text-gray-300',
      sectionTitle: 'text-white',
      heroGradient: 'from-blue-700 to-blue-900',
      philosophyCard: 'bg-blue-900 bg-opacity-30',
      philosophyTitle: 'text-blue-300',
      missionCard: 'bg-green-900 bg-opacity-30',
      missionTitle: 'text-green-300',
      leaderCard: 'bg-gray-800',
      leaderName: 'text-white',
      leaderRole: 'text-blue-400',
      leaderTitle: 'text-gray-400',
      leaderBio: 'text-gray-300',
      leaderExpertise: 'text-gray-400',
      valueCard: 'bg-gray-800',
      valueTitle: 'text-white',
      valueDescription: 'text-gray-300',
      valueIconBg: {
        trust: 'bg-blue-900 bg-opacity-50 text-blue-300',
        innovation: 'bg-purple-900 bg-opacity-50 text-purple-300',
        excellence: 'bg-green-900 bg-opacity-50 text-green-300',
        global: 'bg-amber-900 bg-opacity-50 text-amber-300'
      }
    }
  };

  // Get current theme styles, default to dark if in system mode
  const currentTheme = theme === 'system' 
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  const styles = themeStyles[currentTheme as keyof typeof themeStyles] || themeStyles.dark;

  return (
    <div className={`min-h-screen transition-colors duration-300 ${styles.background} ${styles.text}`}>
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${styles.heroGradient} text-white py-20`}>
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-5xl font-bold mb-6">About Clysera Technologies</h1>
          <p className="text-xl max-w-3xl mx-auto">
            Empowering businesses with next-generation IT solutions built on trust, innovation, and excellence.
          </p>
        </div>
      </div>

      {/* Who We Are */}
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-5xl mx-auto">
          <h2 className={`text-3xl font-bold mb-6 ${styles.sectionTitle}`}>Who We Are</h2>
          <div className={`${styles.card} rounded-xl shadow-lg p-8 mb-12`}>
            <p className={`text-lg ${styles.cardText} leading-relaxed mb-6`}>
              At Clysera, we believe technology is more than a tool — it's the driving force behind business transformation. 
              Founded with the vision of combining clarity and innovation, we partner with enterprises and startups 
              worldwide to deliver IT solutions that create measurable impact.
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 mt-10">
              <div className={`${styles.philosophyCard} p-6 rounded-lg transition-colors duration-300`}>
                <h3 className={`text-xl font-semibold mb-4 ${styles.philosophyTitle}`}>Our Vision</h3>
                <p className={styles.cardText}>
                  To be a global leader in technology services, enabling businesses to achieve clarity, innovation, and 
                  sustainable growth in the digital era.
                </p>
              </div>
              <div className={`${styles.missionCard} p-6 rounded-lg transition-colors duration-300`}>
                <h3 className={`text-xl font-semibold mb-4 ${styles.missionTitle}`}>Our Mission</h3>
                <p className={styles.cardText}>
                  To empower clients with next-generation IT consulting, software development, cloud, AI, and digital 
                  transformation solutions — built on trust, innovation, and excellence.
                </p>
              </div>
            </div>
          </div>

          {/* Our Philosophy */}
          <div className="mb-16">
            <h2 className={`text-3xl font-bold mb-6 ${styles.sectionTitle}`}>Our Philosophy</h2>
            <div className={`${styles.card} rounded-xl shadow-lg p-8 transition-colors duration-300`}>
              <p className={`${styles.cardText} leading-relaxed mb-6`}>
                At Clysera, we believe that technology should bring clarity to complexity. Businesses today face fast-changing 
                markets, new customer expectations, and disruptive technologies. Our philosophy is to simplify these challenges 
                and turn them into opportunities for growth.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Innovation lies at the heart of everything we do. By combining cutting-edge technologies such as AI, cloud, 
                data analytics, automation, and cybersecurity with deep industry expertise, we help our clients not only adapt 
                but also lead with confidence in the digital era.
              </p>
            </div>
          </div>

          {/* Leadership Team */}
          <div className="mb-16">
            <h2 className={`text-3xl font-bold mb-8 text-center ${styles.sectionTitle}`}>Leadership Team</h2>
            <p className={`text-xl text-center ${styles.cardText} mb-12`}>Meet the visionaries behind Clysera Technologies</p>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  initials: 'RM',
                  name: 'Ravi Mishra',
                  role: 'Co-Founder',
                  title: 'Leading Strategy, Cloud, Security and Transformation',
                  bio: 'Ravi Mishra is a seasoned IT leader with deep expertise in cloud computing, IT modernization, and enterprise technology transformation. He brings years of experience driving large-scale digital initiatives across industries, helping businesses achieve efficiency, scalability, and resilience through cutting-edge IT solutions.',
                  expertise: 'With hands-on experience in Google Cloud, ServiceNow, Kubernetes, and Red Hat OpenShift, Ravi specializes in application modernization, cloud migration, and designing secure, high-performance architectures.'
                },
                {
                  initials: 'SS',
                  name: 'Sudhanshu Shekhar',
                  role: 'Co-Founder',
                  title: 'Leading Quality, Delivery and Compliance',
                  bio: 'Sudhanshu is a seasoned quality assurance and testing expert with over 8 years of experience across the full product life cycle. He ensures that every solution Clysera delivers meets the highest benchmarks of performance, compliance, and user experience.',
                  expertise: 'With expertise in Agile/Scrum methodologies, SDLC/STLC processes, and tools like JIRA, Jenkins CI/CD, JMeter, and Postman, Sudhanshu anchors the company\'s quality engineering and delivery excellence.'
                },
                {
                  initials: 'MK',
                  name: 'Mushahid Khan',
                  role: 'Co-Founder',
                  title: 'Leading Engineering, Product Innovation, and Full-Stack Development',
                  bio: 'Mushahid is a skilled technology leader with strong expertise in software engineering, enterprise application development, and mobile solutions. He has extensive experience in building scalable platforms using Java, Angular, Spring Boot, and microservices, along with hands-on proficiency in mobile development with React Native.',
                  expertise: 'He specializes in designing and delivering secure, high-performing, and cloud-ready applications, with a proven ability to adapt quickly to emerging technologies and translate business requirements into innovative solutions.'
                }
              ].map((member, index) => (
                <div key={index} className={`${styles.leaderCard} rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
                  <div className="bg-blue-600 h-2"></div>
                  <div className="p-6">
                    <div className={`w-20 h-20 ${theme === 'dark' ? 'bg-blue-900 bg-opacity-50' : 'bg-blue-100'} rounded-full flex items-center justify-center text-2xl font-bold ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'} mx-auto mb-4`}>
                      {member.initials}
                    </div>
                    <h3 className={`text-xl font-bold text-center ${styles.leaderName}`}>{member.name}</h3>
                    <p className={`${styles.leaderRole} font-medium text-center mb-1`}>{member.role}</p>
                    <p className={`${styles.leaderTitle} text-sm text-center mb-4`}>{member.title}</p>
                    <p className={`${styles.leaderBio} mb-4`}>{member.bio}</p>
                    <p className={`${styles.leaderExpertise} text-sm`}>{member.expertise}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-16">
            <h2 className={`text-3xl font-bold mb-8 text-center ${styles.sectionTitle}`}>Our Core Values</h2>
            <p className={`text-xl text-center ${styles.cardText} mb-12`}>The principles that guide everything we do</p>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                {
                  title: 'Trust',
                  description: 'We build long-lasting partnerships with clients by delivering on our promises with transparency, accountability, and reliability.',
                  icon: '🤝',
                  color: 'bg-blue-50 text-blue-600'
                },
                {
                  title: 'Innovation',
                  description: 'Technology evolves every day — and so do we. We constantly explore emerging technologies to create smarter, future-ready solutions.',
                  icon: '💡',
                  color: 'bg-purple-50 text-purple-600'
                },
                {
                  title: 'Excellence',
                  description: 'Every solution we deliver reflects our commitment to quality, precision, and continuous improvement.',
                  icon: '🏆',
                  color: 'bg-green-50 text-green-600'
                },
                {
                  title: 'Global Mindset',
                  description: 'Our approach is shaped by a global vision. We design solutions that are scalable, adaptable, and impactful worldwide.',
                  icon: '🌍',
                  color: 'bg-amber-50 text-amber-600'
                }
              ].map((value, index) => (
                <div key={index} className={`${styles.valueCard} p-6 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}>
                  <div className={`w-12 h-12 rounded-full ${
                    (value.title.toLowerCase() === 'trust' && styles.valueIconBg.trust) ||
                    (value.title.toLowerCase() === 'innovation' && styles.valueIconBg.innovation) ||
                    (value.title.toLowerCase() === 'excellence' && styles.valueIconBg.excellence) ||
                    (value.title.toLowerCase() === 'global' && styles.valueIconBg.global) ||
                    styles.valueIconBg.trust
                  } flex items-center justify-center text-2xl mb-4`}>
                    {value.icon}
                  </div>
                  <h4 className={`text-xl font-semibold mb-2 ${styles.valueTitle}`}>{value.title}</h4>
                  <p className={styles.valueDescription}>{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
